import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { useProfileInAppNotifications } from '../../../../../hooks/api/useProfileInAppNotifications';
import { useNotificationListState } from '../../../../shared/InAppNotificationsList/useNotificationListState';
import InAppNotificationsList from '../../../../shared/InAppNotificationsList';
import { useNavigateOnInAppNotificationPress } from '../../../../shared/InAppNotificationCard';

const intl = getIntl('profile-messages-screen', {
    sendMessage: 'Send Message',
    sendProfileMessage: 'Send Profile Message',
    edit: 'Edit',
    markRead: 'Mark Read',
    markUnread: 'Mark Unread',
    markAllRead: 'Mark All Read',
    cancel: 'Cancel',
});

export type ProfileMessagesScreenOwnProps = {};

export type ProfileMessagesScreenProps = ProfileMessagesScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'messages'>;
};

const ProfileMessagesScreen: React.FC<ProfileMessagesScreenProps> = ({
    route: {
        params: { profileId },
    },
}) => {
    const navigateOnInAppNotificationOnPress = useNavigateOnInAppNotificationPress();
    const { navigate } = useProfilesTabNavigation();
    const { isLoadingNotifications, allNotifications, toggleReadStatus, isTogglingReadStatus } =
        useProfileInAppNotifications({
            medicalProfileId: profileId,
        });
    const {
        getIsSelected,
        isSelecting,
        toggleIsSelecting,
        canMarkRead,
        canMarkUnread,
        markRead,
        markUnread,
        onToggleSelect,
        canMarkAllRead,
        onMarkAllRead,
        displayNotifications,
        searchText,
        onSearchTextChange,
        onReadStatusFilterValueChange,
        readStatusFilterValue,
        hasAnyProfileNotifications,
        sortOrder,
        onSortOrderChange,
    } = useNotificationListState({
        notifications: allNotifications,
        onPersistReadUnread: (notifications) => {
            toggleReadStatus({
                notificationsToToggle: notifications,
            });
        },
    });

    return (
        <ActionButtonLayout
            actions={[
                {
                    show: !isSelecting,
                    icon: 'Send',
                    testID: 'send-message-action',
                    title: intl('sendMessage'),
                    onPress: () => {
                        navigate('message-profile', {
                            profileId,
                        });
                    },
                    variant: 'primary',
                },
                {
                    show: !isSelecting,
                    icon: 'Edit',
                    testID: 'notifications-mark-read-unread-action',
                    title: intl('edit'),
                    onPress: toggleIsSelecting,
                    variant: 'primary',
                },
                {
                    show: isSelecting && canMarkAllRead,
                    icon: 'Check',
                    testID: 'notifications-mark-all-read-action',
                    title: intl('markAllRead'),
                    onPress: onMarkAllRead,
                    variant: 'primary',
                },
                {
                    show: isSelecting && canMarkRead,
                    icon: 'Check',
                    testID: 'notifications-mark-read-action',
                    title: intl('markRead'),
                    onPress: markRead,
                    variant: 'primary',
                },
                {
                    show: isSelecting && canMarkUnread,
                    icon: 'Check',
                    testID: 'notifications-mark-unread-action',
                    title: intl('markUnread'),
                    onPress: markUnread,
                    variant: 'primary',
                },
                {
                    show: isSelecting,
                    icon: 'X',
                    testID: 'notifications-cancel-action',
                    title: intl('cancel'),
                    onPress: toggleIsSelecting,
                    variant: 'secondary',
                },
            ]}
        >
            <InAppNotificationsList
                profileId={profileId}
                onNotificationPress={navigateOnInAppNotificationOnPress}
                isSelectable={isSelecting}
                getIsSelected={getIsSelected}
                onToggleSelect={onToggleSelect}
                notifications={displayNotifications}
                isLoadingNotifications={isLoadingNotifications || isTogglingReadStatus}
                searchText={searchText}
                onSearchTextChange={onSearchTextChange}
                readStatusFilterValue={readStatusFilterValue}
                onReadStatusFilterValueChange={onReadStatusFilterValueChange}
                hasAnyProfileNotifications={hasAnyProfileNotifications}
                sortOrder={sortOrder}
                onSortOrderChange={onSortOrderChange}
                onMarkAsReadWhenNotificationPressed={(notification) => {
                    toggleReadStatus({
                        notificationsToToggle: [notification],
                        preventSuccessNotification: true,
                    });
                }}
            />
        </ActionButtonLayout>
    );
};

export default React.memo(ProfileMessagesScreen);
